import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';
import { CONFIG } from '../utils/config';

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recuperationId = searchParams.get('recuperation-id') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tokenMissing = useMemo(() => !recuperationId, [recuperationId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (tokenMissing) {
      setFeedback({
        type: 'error',
        message: content.feedback.invalidLink,
      });
      return;
    }

    if (!newPassword || !confirmPassword) {
      setFeedback({ type: 'error', message: content.feedback.missingFields });
      return;
    }

    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: content.feedback.passwordMismatch });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      const body = new URLSearchParams({
        password_reset_id: recuperationId,
        new_password: newPassword,
      }).toString();

      const response = await fetch(`${CONFIG.BASE_URL}/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      const isJson = (response.headers.get('content-type') || '').includes('application/json');
      const data = isJson ? await response.json() : {};

      if (!response.ok) {
        throw new Error(data?.error || content.feedback.updateError );
      }

      setFeedback({
        type: 'success',
        message: content.feedback.success,
      });
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || content.feedback.genericError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { isDark } = useTheme();
  const { content, loading: contentLoading } = useContent('passwordReset');

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Quikr.it Logo"
              className="w-40 h-20 object-contain"
            />
          </Link>

          <p className="text-gray-400 mt-2">
	    {content.pageTitle}
          </p>
        </div>

        <div className="bg-dark-800 p-8 rounded-xl border border-dark-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {feedback.message && (
              <div
                className={`px-4 py-3 rounded-lg text-sm border ${
                  feedback.type === 'success'
                    ? 'bg-green-900/40 border-green-700 text-green-200'
                    : 'bg-red-900/40 border-red-700 text-red-200'
                }`}
              >
                {feedback.message}
              </div>
            )}

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.newPasswordLabel}
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder={content.form.newPasswordPlaceholder}
                required
              />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
		  {content.form.confirmPasswordLabel}
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder={content.form.comfrimPasswordPlaceholder}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || tokenMissing}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
            >
              {isSubmitting ? content.form.submitting : content.form.submitButton }
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            <Link to="/login" className="text-primary-400 hover:text-primary-300">
		{content.footerLink}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
